import { useEffect, useState, useContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoImg from "../assets/logo.jpeg";
import { AuthContext } from "../context/AuthContext";
import {
  BookCopy,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  ClipboardList,
  GraduationCap,
  LayoutGrid,
  Menu,
  ShieldCheck,
  LogOut,
  Building2,
  Clock,
  UserCheck,
  FileText,
  Lock
} from "lucide-react";
import axios from "../api/axios";

// Direct exports from MasterCssClass.js
import {
  masterAmbientGradient1,
  masterAmbientGradient2,
  masterSidebarMobileBtn,
  masterSidebarOverlay,
  masterSidebarPanel,
  masterSidebarScrollArea,
  masterSidebarPortalHeading,
  masterSidebarExamBtnActive,
  masterSidebarExamBtnInactive,
  masterSidebarCategoryGroup,
  masterSidebarCatBtnActive,
  masterSidebarCatBtnInactive,
  masterSidebarFeatureBox,
  masterSidebarFeatureToggleBtn,
  masterSidebarTreeIndented,
  masterSidebarLinkActive,
  masterSidebarLinkInactive,
  masterSidebarStaticFeatureTag,
  masterSidebarAdminToggleBtn,
  masterSidebarAdminLinkActive,
  masterSidebarAdminLinkInactive,
  masterSidebarBottomContainer,
  masterSidebarLogoutBtn,
  masterSidebarSecurityBadge,
} from "./MasterCssClass";

const Sidebar = ({ collapsed = false, onToggleCollapse = () => {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);
  const [menuData, setMenuData] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [openExams, setOpenExams] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [openSyllabus, setOpenSyllabus] = useState({});
  const [openMockTest, setOpenMockTest] = useState({});
  const [adminOpen, setAdminOpen] = useState(false);
  const [unaccessOpen, setUnaccessOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Store last-seen item signatures (string of IDs) in localStorage to track changes
  const [seenFeatures, setSeenFeatures] = useState(() => {
    try {
      const saved = localStorage.getItem(`seen_features_signatures_${user?._id || 'guest'}`);
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });

  const markAsSeen = (uniqueKey, signature) => {
    const updated = { ...seenFeatures, [uniqueKey]: signature };
    setSeenFeatures(updated);
    try {
      localStorage.setItem(`seen_features_signatures_${user?._id || 'guest'}`, JSON.stringify(updated));
    } catch (e) {}
  };

  const isMainAdmin =
    String(user?.email || "").toLowerCase() === "admin@gmail.com" ||
    String(user?.role || "").toLowerCase() === "admin";

  const isOrganization = String(user?.role || "").toLowerCase() === "organization";

  useEffect(() => {
    const fetchMenuAndProgress = async () => {
      try {
        const [menuRes, videoRes] = await Promise.allSettled([
          axios.get("/public/sidebar-menu"),
          axios.get("/video-master/all").catch(() => null),
        ]);

        if (menuRes.status === "fulfilled" && menuRes.value.data.success) {
          setMenuData(Array.isArray(menuRes.value.data.data) ? menuRes.value.data.data : []);
        }

        if (videoRes.status === "fulfilled" && videoRes.value?.data?.success) {
          setAllVideos(videoRes.value.data.data || []);
        }
      } catch (err) {
        console.error("Sidebar sync failed", err);
      }
    };
    if (!isOrganization) {
      fetchMenuAndProgress();
    }
  }, [user, isMainAdmin, isOrganization, location.pathname]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleSidebarLogout = () => {
    if (logout) {
      logout();
    }
    navigate("/login");
  };

  const toggleExam = (e, id) => {
    e.stopPropagation();
    setOpenExams((prev) => (prev[id] ? {} : { [id]: true }));
  };

  const toggleCategory = (e, id) => {
    e.stopPropagation();
    setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleSyllabus = (e, id) => {
    e.stopPropagation();
    setOpenSyllabus((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const isActive = (path) => location.pathname === path;

  const panelClass = mobileOpen
    ? "translate-x-0"
    : collapsed
      ? "-translate-x-full lg:-translate-x-full"
      : "-translate-x-full lg:translate-x-0";

  return (
    <>
      <button
        type="button"
        onClick={() => setMobileOpen(true)}
        className={masterSidebarMobileBtn}
      >
        <Menu size={18} />
      </button>

      {mobileOpen && (
        <button
          type="button"
          aria-label="Close sidebar overlay"
          onClick={() => setMobileOpen(false)}
          className={masterSidebarOverlay}
        />
      )}

      <div className={`${masterSidebarPanel} ${panelClass}`}>
        <div className={masterAmbientGradient1} />
        <div className={masterAmbientGradient2} />
        
        <div className={masterSidebarScrollArea}>
          <Link
            to={isMainAdmin ? "/dashboard" : isOrganization ? "/organization-dashboard" : "/student"}
            className={`flex items-center gap-3 py-2 px-3 rounded-xl transition-all shadow-xs border ${
              isActive("/dashboard") || isActive("/organization-dashboard") || isActive("/student")
                ? "bg-[#0E3652] text-white border-transparent shadow-sm"
                : "bg-white text-slate-800 border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="h-8 w-8 overflow-hidden rounded-lg border-2 border-[#F4B429] shadow-sm flex items-center justify-center shrink-0">
              <img src={logoImg} alt="Logo" className="h-full w-full object-cover" />
            </div>
            <span className="font-black text-xs tracking-wider uppercase">MEDHASHALA</span>
          </Link>

          {/* EXAMS PORTAL: Hidden for Organizations */}
          {!isOrganization && (
            <div>
              <h3 className={masterSidebarPortalHeading}>Exams Portal</h3>
              <div className="space-y-1">
                {menuData.map((exam) => (
                  <div key={exam._id} className="space-y-1">
                    <button
                      type="button"
                      onClick={(e) => toggleExam(e, exam._id)}
                      className={openExams[exam._id] ? masterSidebarExamBtnActive : masterSidebarExamBtnInactive}
                    >
                      <div className="flex items-center gap-2">
                        <GraduationCap size={16} className={openExams[exam._id] ? "text-[#F4B429]" : "text-[#0E3652]"} />
                        <span>{exam.examName}</span>
                      </div>
                      {openExams[exam._id] ? <ChevronDown size={14} strokeWidth={3} /> : <ChevronRight size={14} strokeWidth={3} />}
                    </button>

                    {openExams[exam._id] && (
                      <div className={masterSidebarCategoryGroup}>
                        {exam.categories?.map((cat) => (
                          <div key={cat._id}>
                            <button
                              type="button"
                              onClick={(e) => toggleCategory(e, cat._id)}
                              className={openCategories[cat._id] ? masterSidebarCatBtnActive : masterSidebarCatBtnInactive}
                            >
                              <span>{cat.catName}</span>
                              {openCategories[cat._id] ? <ChevronDown size={12} strokeWidth={3} /> : <ChevronRight size={12} strokeWidth={3} />}
                            </button>

                            {openCategories[cat._id] && (
                              <div className={masterSidebarFeatureBox}>
                                {Array.from(new Set([
                                  ...(cat.features || []), 
                                  "PRACTICE SET", 
                                  "MOCK TEST", 
                                  ...(cat.subjects?.length ? ["SYLLABUS"] : []), 
                                  "VIDEOS", 
                                  ...(cat.pyqEntries?.length ? ["PYQ"] : []), 
                                  ...(cat.studyMaterialEntries?.length ? ["STUDY MATERIAL"] : [])
                                ])).map((feat) => {
                                  const featureUpper = String(feat).toUpperCase();
                                  
                                  if (featureUpper === "PRACTICE SET") {
                                    const basePath = isMainAdmin ? "/dashboard" : "/student";
                                    const entries = cat.practiceSetEntries || [];
                                    const currentCount = entries.length;
                                    if (currentCount === 0) return null;

                                    const viewPath = `${basePath}/mock-test-view/${cat._id || cat.catId}?examCode=${encodeURIComponent(cat.examCode || exam.examCode)}&type=PRACTICE`;
                                    const contentSignature = entries.map(e => e._id || e.id || JSON.stringify(e)).sort().join("_");
                                    const uniqueKey = `practice-set-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    return (
                                      <Link
                                        key={`${cat._id}-practice-set`}
                                        to={viewPath}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markAsSeen(uniqueKey, contentSignature);
                                        }}
                                        className={isActive(viewPath) ? masterSidebarLinkActive : masterSidebarLinkInactive}
                                      >
                                        <span className="flex items-center justify-between w-full">
                                          <span className="flex items-center gap-2">
                                            <CheckCircle2 size={10} className="text-[#0E3652]" /> PRACTICE SET ({currentCount})
                                          </span>
                                          {hasNewContent && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0 ml-2" title="New content available!" />
                                          )}
                                        </span>
                                      </Link>
                                    );
                                  }

                                  if (featureUpper === "MOCK TEST") {
                                    const basePath = isMainAdmin ? "/dashboard" : "/student";
                                    const entries = cat.mockTestEntries || [];
                                    const currentCount = entries.length;
                                    if (currentCount === 0) return null;

                                    const viewPath = `${basePath}/mock-test-view/${cat._id || cat.catId}?examCode=${encodeURIComponent(cat.examCode || exam.examCode)}&type=MOCK`;
                                    const contentSignature = entries.map(e => e._id || e.id || JSON.stringify(e)).sort().join("_");
                                    const uniqueKey = `mock-test-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    return (
                                      <Link
                                        key={`${cat._id}-mock-test`}
                                        to={viewPath}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markAsSeen(uniqueKey, contentSignature);
                                        }}
                                        className={isActive(viewPath) ? masterSidebarLinkActive : masterSidebarLinkInactive}
                                      >
                                        <span className="flex items-center justify-between w-full">
                                          <span className="flex items-center gap-2">
                                            <CheckCircle2 size={10} className="text-[#0E3652]" /> MOCK TEST ({currentCount})
                                          </span>
                                          {hasNewContent && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0 ml-2" title="New content available!" />
                                          )}
                                        </span>
                                      </Link>
                                    );
                                  }

                                  if (featureUpper === "SYLLABUS") {
                                    const subjects = cat.subjects || [];
                                    const currentCount = subjects.length;
                                    if (currentCount === 0) return null;

                                    const contentSignature = subjects.map(s => s.syllabusId || s._id).sort().join("_");
                                    const uniqueKey = `syllabus-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    return (
                                      <div key={`${cat._id}-syllabus`} className="space-y-1">
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleSyllabus(e, cat._id);
                                            markAsSeen(uniqueKey, contentSignature);
                                          }}
                                          className={masterSidebarFeatureToggleBtn}
                                        >
                                          <span className="flex items-center gap-2">
                                            <CheckCircle2 size={10} className="text-[#0E3652]" /> SYLLABUS
                                          </span>
                                          <div className="flex items-center gap-1.5">
                                            {hasNewContent && <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0" />}
                                            {openSyllabus[cat._id] ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
                                          </div>
                                        </button>
                                        {openSyllabus[cat._id] && (
                                          <div className={masterSidebarTreeIndented}>
                                            {subjects.map((subject) => (
                                              <Link
                                                key={subject.syllabusId}
                                                to={`${isMainAdmin ? "/dashboard" : "/student"}/syllabus-view/${subject.syllabusId}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className={
                                                  isActive(`/dashboard/syllabus-view/${subject.syllabusId}`) || isActive(`/student/syllabus-view/${subject.syllabusId}`)
                                                    ? masterSidebarLinkActive
                                                    : masterSidebarLinkInactive
                                                }
                                              >
                                                <div className="flex flex-col">
                                                  <span>{subject.subjectName}</span>
                                                  <span className={`text-[9px] font-semibold ${
                                                    isActive(`/dashboard/syllabus-view/${subject.syllabusId}`) || isActive(`/student/syllabus-view/${subject.syllabusId}`) ? "text-[#F4B429]/90" : "text-slate-500"
                                                  }`}>
                                                    {subject.examStage ? `STAGE: ${subject.examStage}` : "STAGE: N/A"}
                                                  </span>
                                                </div>
                                              </Link>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  if (featureUpper === "VIDEOS") {
                                    const matchingVideos = allVideos.filter(
                                      (v) =>
                                        String(v.examMasterId || "").toUpperCase() === String(cat.examCode || exam.examCode || "").toUpperCase() &&
                                        (
                                          String(v.examCategoryId || "").toUpperCase() === String(cat.catId || cat._id || "").toUpperCase() ||
                                          String(v.catName || "").toUpperCase() === String(cat.catName || "").toUpperCase()
                                        ) &&
                                        String(v.status || "ACTIVE").toUpperCase() === "ACTIVE"
                                    );

                                    const matchingVideosCount = matchingVideos.length;
                                    if (matchingVideosCount === 0) return null;

                                    const contentSignature = matchingVideos.map(v => v._id || v.id).sort().join("_");
                                    const uniqueKey = `videos-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    const videoViewPath = `${isMainAdmin ? "/dashboard" : "/student"}/video-view/${cat.examCode}?catId=${encodeURIComponent(cat.catId || cat._id)}&catName=${encodeURIComponent(cat.catName)}`;

                                    return (
                                      <Link
                                        key={`${cat._id}-videos`}
                                        to={videoViewPath}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markAsSeen(uniqueKey, contentSignature);
                                        }}
                                        className={
                                          isActive(`/dashboard/video-view/${cat.examCode}`) || isActive(`/student/video-view/${cat.examCode}`)
                                            ? masterSidebarLinkActive
                                            : masterSidebarLinkInactive
                                        }
                                      >
                                        <span className="flex items-center justify-between w-full">
                                          <span className="flex items-center gap-2">
                                            <CheckCircle2 size={10} className="text-[#0E3652]" /> VIDEOS ({matchingVideosCount})
                                          </span>
                                          {hasNewContent && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0 ml-2" />
                                          )}
                                        </span>
                                      </Link>
                                    );
                                  }

                                  if (featureUpper === "PYQ") {
                                    const entries = cat.pyqEntries || [];
                                    if (entries.length === 0) return null;

                                    const basePath = isMainAdmin ? "/dashboard" : "/student";
                                    const viewPath = `${basePath}/pyq-view/${cat._id || cat.catId}?examCode=${encodeURIComponent(cat.examCode || exam.examCode)}`;
                                    const contentSignature = entries.map(e => e._id).sort().join("_");
                                    const uniqueKey = `pyq-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    return (
                                      <Link
                                        key={`${cat._id}-pyq-link`}
                                        to={viewPath}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markAsSeen(uniqueKey, contentSignature);
                                        }}
                                        className={isActive(viewPath) ? masterSidebarLinkActive : masterSidebarLinkInactive}
                                      >
                                        <span className="flex items-center justify-between w-full">
                                          <span className="flex items-center gap-2">
                                            <FileText size={10} className="text-[#0E3652]" /> PYQ DOCUMENTS ({entries.length})
                                          </span>
                                          {hasNewContent && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0 ml-2" title="New content available!" />
                                          )}
                                        </span>
                                      </Link>
                                    );
                                  }

                                  if (featureUpper === "STUDY MATERIAL") {
                                    const entries = cat.studyMaterialEntries || [];
                                    if (entries.length === 0) return null;

                                    const basePath = isMainAdmin ? "/dashboard" : "/student";
                                    const viewPath = `${basePath}/study-material-view/${cat._id || cat.catId}?examCode=${encodeURIComponent(cat.examCode || exam.examCode)}`;
                                    const contentSignature = entries.map(e => e._id).sort().join("_");
                                    const uniqueKey = `study-material-${cat._id || cat.catId}`;
                                    const hasNewContent = seenFeatures[uniqueKey] !== contentSignature;

                                    return (
                                      <Link
                                        key={`${cat._id}-mat-link`}
                                        to={viewPath}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          markAsSeen(uniqueKey, contentSignature);
                                        }}
                                        className={isActive(viewPath) ? masterSidebarLinkActive : masterSidebarLinkInactive}
                                      >
                                        <span className="flex items-center justify-between w-full">
                                          <span className="flex items-center gap-2">
                                            <FileText size={10} className="text-[#0E3652]" /> STUDY MATERIALS ({entries.length})
                                          </span>
                                          {hasNewContent && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-sm shadow-red-600 animate-pulse shrink-0 ml-2" title="New content available!" />
                                          )}
                                        </span>
                                      </Link>
                                    );
                                  }

                                  return null;
                                })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* FACULTY DIRECTORY PORTAL LINK: Available for both Main Admin & Organizations (Tenant key wise) */}
          <div className="mt-3 pt-2 border-t border-slate-200">
            {(() => {
              const basePath = isMainAdmin
                ? "/dashboard"
                : isOrganization
                ? "/organization-dashboard"
                : "/student";
              const viewPath = `${basePath}/faculty-view`;

              return (
                <Link
                  to={viewPath}
                  className={isActive(viewPath) ? masterSidebarLinkActive : masterSidebarLinkInactive}
                >
                  <span className="flex items-center gap-2 font-bold uppercase tracking-wider text-xs">
                    <UserCheck size={14} className="text-[#0E3652]" /> Faculty
                  </span>
                </Link>
              );
            })()}
          </div>

          {/* UNACCESS / SYSTEM ADMIN MENU (Main Admin Only) */}
          {isMainAdmin && (
            <>
              <hr className="border-slate-200 my-2" />
              <div>
                <button
                  type="button"
                  onClick={() => setUnaccessOpen(!unaccessOpen)}
                  className={masterSidebarAdminToggleBtn}
                >
                  <span className="flex items-center gap-1.5"><Lock size={12} /> Admin Private</span>
                  {unaccessOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {unaccessOpen && (
                  <div className="space-y-0.5">
                    {[
                      { to: "login-history", perm: "login-history", icon: Clock, label: "Login Activity" },
                      { to: "organization-approvals", perm: "organization-approvals", icon: Building2, label: "Org Approvals" },
                    ].map((item) => {
                      const fullPath = `/dashboard/${item.to}`;
                      return (
                        <Link
                          key={fullPath}
                          to={fullPath}
                          className={isActive(fullPath) ? masterSidebarAdminLinkActive : masterSidebarAdminLinkInactive}
                        >
                          <item.icon size={16} strokeWidth={2.5} /> {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* CONTROL PANEL FOR ADMIN & PERMITTED ORGANIZATIONS */}
          {(isMainAdmin || isOrganization) && (
            <>
              <hr className="border-slate-200" />
              <div>
                <button
                  type="button"
                  onClick={() => setAdminOpen(!adminOpen)}
                  className={masterSidebarAdminToggleBtn}
                >
                  <span>Control Panel</span>
                  {adminOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {adminOpen && (
                  <div className="space-y-0.5">
                    {[
                      { to: "exam-master", perm: "exam-master", icon: BookOpen, label: "Exam Master" },
                      { to: "category-master", perm: "category-master", icon: LayoutGrid, label: "Category Master" },
                      { to: "syllabus-master", perm: "syllabus-master", icon: BookCopy, label: "Syllabus Master" },
                      { to: "question-bank", perm: "question-bank", icon: CircleHelp, label: "Question Bank" },
                      { to: "mock-test", perm: "mock-test", icon: ClipboardList, label: "Mock & Practice Set" },
                      { to: "video-master", perm: "video-master", icon: BookOpen, label: "Video Master" },
                      { to: "faculty-master", perm: "faculty-master", icon: UserCheck, label: "Faculty Master" },
                      { to: "pyq-master", perm: "pyq-master", icon: FileText, label: "PYQ Master" },
                      { to: "study-material-master", perm: "study-material-master", icon: FileText, label: "Study Material" },
                      { to: "student-approvals", perm: "student-approvals", icon: UserCheck, label: "Student Approvals" },
                      { to: "referral-master", perm: "referral-master", icon: ClipboardList, label: "Referral Generate" },
                    ].filter((item) => {
                      if (isMainAdmin) return true;
                      return Array.isArray(user?.permissions) && user.permissions.includes(item.perm);
                    }).map((item) => {
                      const basePath = isMainAdmin ? "/dashboard" : "/organization-dashboard";
                      const fullPath = `${basePath}/${item.to}`;
                      return (
                        <Link
                          key={fullPath}
                          to={fullPath}
                          className={isActive(fullPath) ? masterSidebarAdminLinkActive : masterSidebarAdminLinkInactive}
                        >
                          <item.icon size={16} strokeWidth={2.5} /> {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* BOTTOM SECTION */}
        <div className={masterSidebarBottomContainer}>
          <button
            type="button"
            onClick={handleSidebarLogout}
            className={masterSidebarLogoutBtn}
          >
            <LogOut size={13} strokeWidth={2.5} /> Logout
          </button>

          <div className={masterSidebarSecurityBadge}>
            <ShieldCheck size={12} strokeWidth={3} /> {isMainAdmin ? "Admin Security Mode" : "Secured Student Area"}
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;